// pages/ConfigPage.js - 系统配置
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Tabs, Form, Input, Button, Switch, InputNumber, Select, message, Divider, Tag, Spin, Table, Modal, Popconfirm } from 'antd';
import { SaveOutlined, PlusOutlined, DeleteOutlined, SettingOutlined, ReloadOutlined } from '@ant-design/icons';
import { Row, Col } from 'antd';
import { api } from '../services/api';

const cardStyle = {
  background: 'linear-gradient(135deg, #0d1f44 0%, #132c5e 100%)',
  border: '1px solid rgba(0, 180, 216, 0.3)',
  borderRadius: 8,
  boxShadow: '0 0 20px rgba(0, 180, 216, 0.1)',
};
const COLORS = { primary: '#00d4ff', text: '#fff', muted: 'rgba(255,255,255,0.6)' };
const sectionTitle = { fontSize: 14, fontWeight: 600, color: COLORS.text, letterSpacing: 2, paddingLeft: 12, borderLeft: '3px solid #00d4ff', marginBottom: 16 };
const inputStyle = { background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,180,216,0.3)', color: '#fff' };
const inputDark = { background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,180,216,0.3)', color: '#fff' };

// 用户管理Tab组件
const UserManagementTab = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [assignedDevices, setAssignedDevices] = useState([]);
  const [deviceModalVisible, setDeviceModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [addForm] = Form.useForm();

  // 当前用户
  const [currentUser] = useState(() => {
    const saved = localStorage.getItem('currentUser');
    return saved ? JSON.parse(saved) : null;
  });
  const isSuperAdmin = currentUser && currentUser.isadmin === 2;

  const loadUsers = async () => {
    const json = await api.getUserList({ pageSize: 100 });
    if (json.success) {
      // 同时加载每个用户的设备列表（单个失败不阻塞其他）
      const usersWithDevices = await Promise.all(
        json.data.map(async (user) => {
          try {
            const devJson = await api.getUserDevices(user.id);
            return { ...user, assignedDevices: devJson.success ? devJson.data : [] };
          } catch {
            return { ...user, assignedDevices: [] };
          }
        })
      );
      setUsers(usersWithDevices);
    }
  };

  const loadDevices = async () => {
    const json = isSuperAdmin
      ? await api.getDeviceInfoListAll({ pageSize: 200 })
      : await api.getDeviceInfoList({ pageSize: 200 });
    if (json.success) {
      setDevices(json.data.map(d => ({ key: String(d.id), title: `${d.sn} - ${d.station_name || ''}`, ...d })));
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      loadUsers();
      loadDevices();
    }
  }, [isSuperAdmin]);

  if (!isSuperAdmin) {
    return <div style={{ color: COLORS.muted, textAlign: 'center', padding: 40 }}>{t('config.noPermission')}</div>;
  }

  const openDeviceAssign = (user) => {
    setSelectedUser(user);
    const devs = user.assignedDevices || [];
    setAssignedDevices(devs.map(d => String(d.device_info_id)));
    setDeviceModalVisible(true);
  };

  const saveDeviceAssign = async () => {
    // 转换为数字数组传给后端
    const numericIds = assignedDevices.map(id => parseInt(id));
    const json = await api.assignUserDevices(selectedUser.id, numericIds);
    if (!json.success) {
      message.error(json.message || t('config.assignFailed'));
      return;
    }
    message.success(t('config.devicesAssigned'));
    // 更新本地用户列表中的设备数据
    const updatedDevs = numericIds.map(id => ({
      device_info_id: id,
      sn: devices.find(d => d.key === String(id))?.title || String(id)
    }));
    setUsers(users => {
      const updated = users.map(u => u.id === selectedUser.id ? { ...u, assignedDevices: updatedDevs } : u);
      // 同步更新 selectedUser
      setSelectedUser(prev => ({ ...prev, assignedDevices: updatedDevs }));
      return updated;
    });
    setDeviceModalVisible(false);
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    editForm.setFieldsValue({
      name: user.name,
      isadmin: user.isadmin,
      phone: user.phone || '',
      email: user.email || '',
      pass: '',
    });
    setEditModalVisible(true);
  };

  const handleEditUser = async (values) => {
    const payload = { ...values };
    if (!payload.pass) delete payload.pass;
    payload.isadmin = parseInt(payload.isadmin);
    const json = await api.updateUser(selectedUser.id, payload);
    if (json.success) {
      message.success(t('config.userUpdated'));
      editForm.resetFields();
      setEditModalVisible(false);
      loadUsers();
    } else {
      message.error(json.message || t('config.userUpdateFailed'));
    }
  };

  const handleAddUser = async (values) => {
    const json = await api.addUser({ ...values, isadmin: parseInt(values.isadmin) });
    if (json.success) {
      message.success(t('config.userAdded'));
      addForm.resetFields();
      loadUsers();
    } else {
      message.error(json.message || t('config.userAddFailed'));
    }
  };

  const handleDeleteUser = async (id) => {
    await api.deleteUser(id);
    message.success(t('config.userDeleted'));
    loadUsers();
  };

  const userColumns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: t('config.username'), dataIndex: 'name' },
    { title: t('config.role'), dataIndex: 'isadmin', render: v => v === 2 ? <Tag color="purple">{t('config.superAdminUser')}</Tag> : v === 1 ? <Tag color="blue">{t('config.adminUser')}</Tag> : <Tag>{t('config.normalUser')}</Tag> },
    { title: t('config.phone'), dataIndex: 'phone' },
    { title: t('config.email'), dataIndex: 'email' },
    {
      title: t('config.assignedDevicesList'),
      key: 'assignedDevices',
      render: (_, record) => {
        const devs = record.assignedDevices || [];
        if (devs.length === 0) return <span style={{ color: COLORS.muted }}>-</span>;
        return (
          <div style={{ maxWidth: 300 }}>
            {devs.slice(0, 5).map((d, i) => (
              <Tag key={i} style={{ marginBottom: 2 }}>{d.sn || d.device_info_id}</Tag>
            ))}
            {devs.length > 5 && <Tag style={{ marginBottom: 2 }}>+{devs.length - 5} more</Tag>}
          </div>
        );
      }
    },
    { title: t('config.createdAt'), dataIndex: 'created_at', render: v => v ? new Date(v).toLocaleString() : '-' },
    {
      title: t('config.actions'),
      width: 280,
      render: (_, record) => (
        <>
          <Button size="small" icon={<SettingOutlined />} onClick={() => openEditModal(record)} style={{ marginRight: 4 }}>
            {t('config.edit')}
          </Button>
          <Button size="small" icon={<ReloadOutlined />} onClick={() => openDeviceAssign(record)} style={{ marginRight: 4 }}>
            {t('config.assignDevices')}
          </Button>
          <Popconfirm title={t('config.confirmDelete')} onConfirm={() => handleDeleteUser(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </>
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, color: COLORS.muted }}><Tag>{t('config.adminOnly')}</Tag></div>
      <Form form={addForm} layout="vertical" onFinish={handleAddUser} autoComplete="off">
        <Row gutter={16}>
          <Col xs={24} md={6}>
            <Form.Item name="name" label={<span style={{ color: COLORS.muted }}>{t('config.username')}</span>} rules={[{ required: true }]}>
              <Input style={inputStyle} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name="pass" label={<span style={{ color: COLORS.muted }}>{t('config.password')}</span>} rules={[{ required: true }]}>
              <Input.Password autoComplete="new-password" style={inputStyle} />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item name="isadmin" label={<span style={{ color: COLORS.muted }}>{t('config.role')}</span>} rules={[{ required: true, message: t('config.selectRole') }]}>
              <Select placeholder={t('config.selectRole')} style={{ width: '100%' }} popupStyle={{ background: '#1a2a5e' }}>
                <Select.Option value={0}>{t('config.normalUser')}</Select.Option>
                <Select.Option value={1}>{t('config.adminUser')}</Select.Option>
                <Select.Option value={2}>{t('config.superAdminUser')}</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item label={<span style={{ color: COLORS.muted }}>{t('config.phone')}</span>} name="phone">
              <Input autoComplete="off" style={inputStyle} />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item label={<span style={{ color: COLORS.muted }}>{t('config.email')}</span>} name="email">
              <Input autoComplete="off" style={inputStyle} />
            </Form.Item>
          </Col>
        </Row>
        <Button type="primary" icon={<PlusOutlined />} htmlType="submit">{t('config.addUser')}</Button>
      </Form>

      <Divider />

      <Table
        columns={userColumns}
        dataSource={users}
        rowKey="id"
        size="small"
        pagination={{ pageSize: 10 }}
      />

      {/* 编辑用户 Modal */}
      <Modal
        title={`${t('config.editUser')} - ${selectedUser?.name}`}
        open={editModalVisible}
        onOk={() => editForm.submit()}
        onCancel={() => setEditModalVisible(false)}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditUser}>
          <Form.Item name="name" label={<span style={{ color: COLORS.muted }}>{t('config.username')}</span>} rules={[{ required: true }]}>
            <Input style={inputStyle} />
          </Form.Item>
          <Form.Item name="isadmin" label={<span style={{ color: COLORS.muted }}>{t('config.role')}</span>} rules={[{ required: true }]}>
            <Select style={{ width: '100%' }} popupStyle={{ background: '#1a2a5e' }}>
              <Select.Option value={0}>{t('config.normalUser')}</Select.Option>
              <Select.Option value={1}>{t('config.adminUser')}</Select.Option>
              <Select.Option value={2}>{t('config.superAdminUser')}</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item label={<span style={{ color: COLORS.muted }}>{t('config.phone')}</span>} name="phone">
            <Input style={inputStyle} />
          </Form.Item>
          <Form.Item label={<span style={{ color: COLORS.muted }}>{t('config.email')}</span>} name="email">
            <Input style={inputStyle} />
          </Form.Item>
          <Form.Item label={<span style={{ color: COLORS.muted }}>{t('config.newPassword')}</span>} name="pass">
            <Input.Password placeholder={t('config.passwordHint')} style={inputStyle} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 分配设备 Modal */}
      <Modal
        title={`${t('config.assignDevices')} - ${selectedUser?.name}`}
        open={deviceModalVisible}
        onOk={saveDeviceAssign}
        onCancel={() => setDeviceModalVisible(false)}
        width={600}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
      >
        <div style={{ marginBottom: 16, color: COLORS.muted, fontSize: 12 }}>
          {t('config.assignedDevices')} ({assignedDevices.length})：
        </div>
        <Select
          mode="multiple"
          placeholder={t('config.selectDevices')}
          value={assignedDevices}
          onChange={(vals) => setAssignedDevices(vals.map(v => String(v)))}
          style={{ width: '100%' }}
          allowClear
        >
          {devices.map(d => (
            <Select.Option key={d.key} value={d.key}>{d.title}</Select.Option>
          ))}
        </Select>
      </Modal>
    </div>
  );
};

const ConfigPage = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [currentUser] = useState(() => {
    const saved = localStorage.getItem('currentUser');
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const res = await api.getConfigList();
      if (res.success && res.data.system) {
        const vals = {};
        res.data.system.forEach(item => { vals[item.key] = item.value; });
        form.setFieldsValue(vals);
      }
    } catch (e) {
      console.error(t('config.loadFailed'), e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values) => {
    try {
      const res = await api.updateConfigBatch(values);
      if (res.success) {
        message.success(t('config.saveSuccess'));
      } else {
        message.error(t('config.saveFailed'));
      }
    } catch (e) {
      message.error(t('config.saveFailed'));
    }
  };

  return (
    <div style={{ minHeight: '100%' }}>
      <Spin spinning={loading}>
      <Card variant="borderless" style={cardStyle} styles={{ body: { padding: 20 } }}>
        <div style={sectionTitle}>{t('config.title')}</div>
        <Tabs
          defaultActiveKey="system"
          items={[
            {
              key: 'system', label: t('config.systemParams'),
              children: (
                <Form form={form} layout="vertical" onFinish={handleSave}>
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item name="power_rate" label={<span style={{ color: COLORS.muted }}>{t('config.powerRate')}</span>}>
                        <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item name="alert_threshold" label={<span style={{ color: COLORS.muted }}>{t('config.alertThreshold')}</span>}>
                        <InputNumber min={0} max={100} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item name="data_interval" label={<span style={{ color: COLORS.muted }}>{t('config.dataInterval')}</span>}>
                        <InputNumber min={10} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item name="log_retention" label={<span style={{ color: COLORS.muted }}>{t('config.logRetention')}</span>}>
                        <InputNumber min={1} max={365} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Divider style={{ borderColor: 'rgba(0,180,216,0.2)' }} />
                  <Form.Item name="enable_alert" label={<span style={{ color: COLORS.muted }}>{t('config.enableAlert')}</span>} valuePropName="checked">
                    <Switch />
                  </Form.Item>
                  <Form.Item name="enable_auto_report" label={<span style={{ color: COLORS.muted }}>{t('config.enableAutoReport')}</span>} valuePropName="checked">
                    <Switch />
                  </Form.Item>
                  <Form.Item>
                    <Button type="primary" icon={<SaveOutlined />} htmlType="submit" style={{ background: 'rgba(0,180,216,0.2)', color: COLORS.primary, border: '1px solid rgba(0,180,216,0.4)' }}>{t('config.saveConfig')}</Button>
                  </Form.Item>
                </Form>
              )
            },
            ...(currentUser && currentUser.isadmin >= 1 ? [{
              key: 'user', label: t('config.userManagement'),
              children: <UserManagementTab />
            }] : []),
            {
              key: 'about', label: t('config.about'),
              children: (
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.primary, marginBottom: 8 }}>{t('config.systemName')}</div>
                    <div style={{ color: COLORS.muted, marginBottom: 8 }}>{t('config.company')}</div>
                    <div style={{ color: COLORS.muted, marginBottom: 8 }}>{t('config.techStack')}</div>
                    <div style={{ color: COLORS.muted }}>{t('config.version')}</div>
                  </div>
                </div>
              )
            }
          ]}
        />
      </Card>
      </Spin>
    </div>
  );
};

export default ConfigPage;
